import useUser from '@auth/useUser';
import { useCallback, useEffect, useRef, useState } from 'react';
import toast from 'react-hot-toast';
import { useIdleTimer } from 'react-idle-timer';

/**
 * IdleTimer component manages user idle state and auto-logout functionality.
 * It tracks user activity and automatically signs out the user after a specified period of inactivity.
 * The timeout can be configured via localStorage, allowing for dynamic updates across multiple tabs.
 * @returns A component that manages user idle state and auto-logout functionality.
 */
function IdleTimer() {

    const [count, setCount] = useState<number>(0);
    const { data: user, signOut } = useUser();
    const timerRunning = useRef<boolean>(false);
    const toastIdRef = useRef<string | null>(null);

    // Use a very high number (max INT32) when disabled, which is approximately 24.8 days
    const MAX_TIMEOUT = 2147483; // Max safe value in seconds (~24.8 days)

    // Get timeout from localStorage or use default (15 minutes)
    // Value is stored in minutes, but we need seconds for the timer
    const [timeoutSeconds, setTimeoutSeconds] = useState<number>(() => {
        const storedValue = localStorage.getItem('idleTimeoutSeconds');
        const isDisabled = storedValue === '-1';
        const timeout = isDisabled ? MAX_TIMEOUT : parseInt(storedValue || '15', 10) * 60;
        return timeout;
    });

    // Store if timer is effectively disabled
    const [timerDisabled, setTimerDisabled] = useState<boolean>(() => {
        return localStorage.getItem('idleTimeoutSeconds') === '-1';
    });

    const timeoutMs = timeoutSeconds * 1000;

    const onIdle = () => {
        signOut();
        cleanupOnLogout();
    };

    const onAction = () => {
        setCount(count + 1);
    };

    const {
        getRemainingTime,
        start,
        reset,
        pause,
    } = useIdleTimer({
        onIdle,
        onAction,
        timeout: timeoutMs,
        throttle: 500,
    });

    // Function to clean up when user is logged out
    const cleanupOnLogout = useCallback(() => {
        reset();
        pause();
        timerRunning.current = false;

        // Dismiss any active toast
        if (toastIdRef.current) {
            toast.dismiss(toastIdRef.current);
            toastIdRef.current = null;
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [reset]);

    // Function to start the timer
    const startTimer = useCallback(() => {
        // Don't start the timer if timeoutSeconds is -1 (disabled)
        if (!timerRunning.current && user && !timerDisabled) {
            start();
            timerRunning.current = true;
        }
    }, [user, start, timerDisabled]);

    // Function to handle storage changes
    const handleStorageChange = useCallback((event: StorageEvent) => {
        if (event.key === 'idleTimeoutSeconds' && event.newValue) {
            const isDisabled = event.newValue === '-1';

            // Convert minutes to seconds, use high value if disabled
            const newTimeout = isDisabled
                ? MAX_TIMEOUT
                : parseInt(event.newValue, 10) * 60;

            setTimerDisabled(isDisabled);
            setTimeoutSeconds(newTimeout);

            if (isDisabled) {
                // If timer is disabled, pause it
                if (timerRunning.current) {
                    cleanupOnLogout();
                }
            } else if (timerRunning.current) {
                // Reset and restart timer with new timeout
                reset();
                start();
            } else if (user) {
                // Start the timer if it wasn't running but should be
                start();
                timerRunning.current = true;
            }
        }
    }, [reset, start, user, cleanupOnLogout]);

    // Listen for localStorage changes from other tabs/windows
    useEffect(() => {
        window.addEventListener('storage', handleStorageChange);

        return () => {
            window.removeEventListener('storage', handleStorageChange);
        };
    }, [handleStorageChange]);

    // Listen for localStorage changes in the current tab
    useEffect(() => {
        const checkLocalStorage = () => {
            const storedValue = localStorage.getItem('idleTimeoutSeconds');
            const isDisabled = storedValue === '-1';

            // Convert minutes to seconds, use high value if disabled
            const storedTimeout = isDisabled
                ? MAX_TIMEOUT
                : parseInt(storedValue || '15', 10) * 60;

            if (storedTimeout !== timeoutSeconds || isDisabled !== timerDisabled) {

                setTimerDisabled(isDisabled);
                setTimeoutSeconds(storedTimeout);

                if (isDisabled) {
                    // If timer is disabled, pause it
                    if (timerRunning.current) {
                        cleanupOnLogout();
                    }
                } else if (timerRunning.current) {
                    // Reset and restart timer with new timeout
                    reset();
                    start();
                } else if (user) {
                    // Start the timer if it wasn't running but should be
                    start();
                    timerRunning.current = true;
                }
            }
        };

        // Check every second for changes
        const interval = setInterval(checkLocalStorage, 1000);

        return () => {
            clearInterval(interval);
        };
    }, [timeoutSeconds, timerDisabled, reset, start, user, cleanupOnLogout]);

    useEffect(() => {
        // Only run timer if user is logged in and timeout is not -1
        if (user && !timerDisabled) {
            startTimer();
        } else {
            if (timerRunning.current) {
                cleanupOnLogout();
            }
        }
    }, [user, cleanupOnLogout, startTimer, timeoutSeconds, timerDisabled]);

    // Update remaining state for internal use
    useEffect(() => {

        if (!user || timerDisabled) {
            // Make sure no toast is showing if the user is logged out or timer is disabled
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            }

            return;
        }

        const interval = setInterval(() => {
            const remainingSeconds = Math.ceil(getRemainingTime() / 1000);

            // Show warning toast when less than 20 seconds remain
            if (remainingSeconds <= 20 && remainingSeconds > 0) {
                // Create a fixed-width message to prevent jumping
                const toastMessage = `Inactive! You will be logged out in ${remainingSeconds.toString().padStart(2, ' ')} seconds...`;

                if (!toastIdRef.current) {
                    // Create a new loading toast
                    toastIdRef.current = toast.loading(
                        toastMessage,
                        {
                            duration: Infinity, // Don't automatically dismiss
                            position: 'top-center',
                            style: {
                                minWidth: '380px', // Fixed width for the toast
                                textAlign: 'left'
                            }
                        }
                    );
                } else {
                    // Update the existing toast with current remaining time
                    toast.loading(toastMessage, {
                        id: toastIdRef.current,
                        style: {
                            minWidth: '380px', // Fixed width for the toast
                            textAlign: 'center'
                        }
                    });
                }
            }
            // If more than 20 seconds remain and we have an active toast, dismiss it
            else if (remainingSeconds > 20 && toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            } else if (remainingSeconds <= 0 && toastIdRef.current) {
                // If the timer has expired, dismiss the toast
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            }

        }, 500);

        return () => {
            clearInterval(interval);

            // Make sure to dismiss any toast if component unmounts
            if (toastIdRef.current) {
                toast.dismiss(toastIdRef.current);
                toastIdRef.current = null;
            }

        };
    }, [getRemainingTime, user, timerDisabled]);

    // This is a logic-only component, it doesn't render anything
    return null;
}

export default IdleTimer;